import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FolderIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { nfsApi } from '../../services/api';
import { NFSServer } from '../../types';

interface NFSShare {
  id?: string;
  serverId: string;
  path: string;
  clients: string[];
  access: 'rw' | 'ro';
  rootSquash: boolean;
  subtreeCheck: boolean;
}

interface ShareFormProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (share: NFSShare) => void;
  share?: NFSShare;
}

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <InformationCircleIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-300 cursor-help" />
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-800 text-zinc-200 text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-64 z-10">
      {text}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-8 border-transparent border-t-zinc-800"></div>
    </div>
  </div>
);

const ShareForm: React.FC<ShareFormProps> = ({
  isOpen,
  onCancel,
  onSave,
  share
}) => {
  const [serverId, setServerId] = useState('');
  const [path, setPath] = useState('');
  const [clients, setClients] = useState('');
  const [access, setAccess] = useState<'rw' | 'ro'>('rw');
  const [rootSquash, setRootSquash] = useState(true);
  const [subtreeCheck, setSubtreeCheck] = useState(false);
  const [servers, setServers] = useState<NFSServer[]>([]);

  useEffect(() => {
    if (isOpen) {
      nfsApi.getAllServers().then(setServers);
      if (share) {
        setServerId(share.serverId);
        setPath(share.path);
        setClients(share.clients.join(', '));
        setAccess(share.access);
        setRootSquash(share.rootSquash);
        setSubtreeCheck(share.subtreeCheck);
      } else {
        setServerId('');
        setPath('');
        setClients('');
        setAccess('rw');
        setRootSquash(true);
        setSubtreeCheck(false);
      }
    }
  }, [isOpen, share]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!serverId || !path) return;
    const shareData: NFSShare = {
      serverId,
      path,
      clients: clients.split(',').map(c => c.trim()).filter(Boolean),
      access,
      rootSquash,
      subtreeCheck,
    };
    if (share && share.id) {
      onSave({ ...shareData, id: share.id });
    } else {
      onSave(shareData);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onCancel}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10 max-h-[90vh] flex flex-col">
                <div className="border-b border-zinc-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FolderIcon className="h-5 w-5 text-indigo-500" />
                      <h2 className="text-lg font-medium text-zinc-100">
                        {share ? 'Edit NFS Share' : 'Add NFS Share'}
                      </h2>
                    </div>
                    <button 
                      type="button"
                      onClick={onCancel}
                      className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                  <div className="p-6 space-y-6 flex-grow overflow-y-auto">
                    {/* Basic Configuration */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-zinc-300">Basic Configuration</h4>
                      <div>
                        <label htmlFor="serverId" className="block text-sm font-medium text-zinc-400 mb-1">
                          Server
                        </label>
                        <select
                          id="serverId"
                          required
                          value={serverId}
                          onChange={e => setServerId(e.target.value)}
                          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-2 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                        >
                          <option value="" disabled>Select a server...</option>
                          {servers.map(srv => (
                            <option key={srv.id} value={srv.id}>{srv.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="path" className="block text-sm font-medium text-zinc-400 mb-1">
                          Path
                        </label>
                        <input
                          type="text"
                          id="path"
                          required
                          value={path}
                          onChange={(e) => setPath(e.target.value)}
                          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                          placeholder="e.g. /data/kubernetes"
                        />
                      </div>
                      <div>
                        <label htmlFor="clients" className="block text-sm font-medium text-zinc-400 mb-1">
                          Clients (comma-separated IPs/hostnames)
                        </label>
                        <input
                          type="text"
                          id="clients"
                          value={clients}
                          onChange={(e) => setClients(e.target.value)}
                          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                          placeholder="e.g. 10.0.0.0/24, k8s-node-1"
                        />
                      </div>
                    </div>

                    {/* Access Settings */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-zinc-300">Access Settings</h4>
                      <div>
                        <label className="block text-sm font-medium text-zinc-400 mb-1">
                          Access
                        </label>
                        <select
                          value={access}
                          onChange={e => setAccess(e.target.value as 'rw' | 'ro')}
                          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-2 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                        >
                          <option value="rw">Read/Write</option>
                          <option value="ro">Read-Only</option>
                        </select>
                      </div>
                    </div>

                    {/* Security Settings */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-zinc-300">Security Settings</h4>
                      <div className="flex flex-col space-y-2">
                        <label className="inline-flex items-center text-sm text-zinc-400">
                          <input
                            type="checkbox"
                            checked={rootSquash}
                            onChange={e => setRootSquash(e.target.checked)}
                            className="mr-2 rounded border-zinc-700 text-indigo-500 focus:ring-indigo-500"
                          />
                          Root Squash
                          <InfoTooltip text="When enabled, requests from root user (UID 0) are mapped to the nobody user. This is a security feature recommended for Kubernetes deployments." />
                        </label>
                        <label className="inline-flex items-center text-sm text-zinc-400">
                          <input
                            type="checkbox"
                            checked={subtreeCheck}
                            onChange={e => setSubtreeCheck(e.target.checked)}
                            className="mr-2 rounded border-zinc-700 text-indigo-500 focus:ring-indigo-500"
                          />
                          Subtree Check
                          <InfoTooltip text="When enabled, the server checks if the requested file is within the exported directory. This can impact performance but provides additional security." />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 px-6 py-4 flex justify-end space-x-3 bg-zinc-900 sticky bottom-0">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                      {share ? 'Update' : 'Add'} Share
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ShareForm; 